import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { readPostAuthReturn } from '../../auth/authReturn';
import { authHeaders, requestJson } from '../../api/http';
import { StateMessage } from '../../components/ui';
import './AuthCallback.css';
import { clearPrivateOfflineData, saveOfflineSnapshot } from '../../api/offlineSession';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { dispatch } = useApp();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    const handleAuthCallback = async () => {
      try {
        const token = searchParams.get('token');
        const userId = searchParams.get('user_id');

        if (!token || !userId) {
          throw new Error('Missing authentication parameters');
        }

        if (localStorage.getItem('user_id') !== userId || localStorage.getItem('auth_token') !== token) await clearPrivateOfflineData();

        // Store token in localStorage
        localStorage.setItem('auth_token', token);
        localStorage.setItem('user_id', userId);

        // Fetch user information
        const apiBase = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';
        const userData = await requestJson<any>(`${apiBase}/auth/me`, { headers: authHeaders(true) });

        await saveOfflineSnapshot('user', { ...userData, isAuthenticated: true, picture: userData.profile_picture_url });

        // Update app context with user data
        dispatch({
          type: 'SET_USER',
          payload: {
            id: userData.id,
            email: userData.email,
            full_name: userData.full_name,
            username: userData.username,
            is_premium: userData.is_premium,
            is_internal_operator: userData.is_internal_operator,
            picture: userData.profile_picture_url,
            isAuthenticated: true,
          },
        });

        const destination = readPostAuthReturn();
        setStatus('success');
        navigate(destination, { replace: true });

      } catch (error) {
        setStatus('error');

        // Redirect to login page after error
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      }
    };

    handleAuthCallback();
  }, [searchParams, dispatch, navigate]);

  return (
    <div className="auth-callback">
      {status === 'processing' && (
        <StateMessage
          kind="loading"
          title="Signing you in…"
          shape="text"
          headingLevel={1}
          testId="auth-callback-processing"
        />
      )}
      {status === 'processing' && <p className="auth-callback__message">Your family account will open in a moment.</p>}
      {status === 'success' && (
        <StateMessage
          kind="empty"
          title="Welcome to Luma"
          message="Your family account is ready. Taking you back…"
          headingLevel={1}
        />
      )}
      {status === 'error' && (
        <StateMessage
          kind="error"
          title="We couldn’t sign you in"
          message="Try again from the Luma home page."
          headingLevel={1}
          retry={{ label: 'Return to sign in', onClick: () => navigate('/login', { replace: true }) }}
        />
      )}
    </div>
  );
};

export default AuthCallback;
